"use client";
import React, { useEffect, useState } from "react";
import { Alert, Box, Container, Skeleton, Tab, Tabs, Typography } from "@mui/material";

import { getAllCruisesListV2 } from "@/services/backOffice";
import { IAllCruisesList, ICruise } from "@/interfaces/IAllCruisesList";
import CruiseSummaryCard from "../home/CruiseSummaryCard";

import { CruiseLineCard } from "./HeroBanner";
import { toTitleCase } from "@/utils/helpers";
export function a11yProps(prefix: string, index: number) {
  return {
    id: `${prefix}-tab-${index}`,
    "aria-controls": `${prefix}-tabpanel-${index}`,
  };
}

export interface ICruisesByRegions {
  [key: string]: ICruise[] | [];
}

const extractPricesFromCruise = (cruise: ICruise) => {
  return [
    { price: Number(cruise.cruiseOnlyPrice), type: "Cruise Only", displayName: "" },
    { price: Number(cruise.flyCruisePrice), type: "Fly Cruise", displayName: "(with Flight)" },
    { price: Number(cruise.insidePrice), type: "Inside Cabin", displayName: "(Inside Cabin)" },
    { price: Number(cruise.outsidePrice), type: "Outside Cabin", displayName: "(Outside Cabin)" },
    { price: Number(cruise.balconyPrice), type: "Balcony", displayName: "(Balcony)" },
    { price: Number(cruise.suitePrice), type: "Suite", displayName: "(Suite)" },
  ].filter((p) => p.price > 0 && !isNaN(p.price)); // Filter out zero/invalid prices
};

export const getLowestPriceInfo = (cruise: ICruise) => {
  const prices = extractPricesFromCruise(cruise);

  if (prices.length === 0) {
    return { price: "0.00", type: "", displayName: "" };
  }
  const sortedPrices = [...prices].sort((a, b) => a.price - b.price);
  const lowest = sortedPrices[0];

  return {
    price: lowest.price.toFixed(2),
    type: lowest.type,
    displayName: lowest.displayName,
  };
};

type CruiseWithLowestPrice = ICruise & {
  lowestPrice: number;
  lowestPriceInfo: {
    price: string;
    type: string;
    displayName: string;
  };
};

const BookOnlineCruiseDeals = () => {
  const [loading, setLoading] = useState(true);
  const [cruisesByRegions, setCruisesByRegions] = useState<IAllCruisesList>();
  const [processedCruises, setProcessedCruises] = useState<CruiseWithLowestPrice[]>([]);

  const regions = [
    "Africa",
    "Alaska",
    "Amazon",
    "Arctic",
    "Asia",
    "Caribbean",
    "Chobe",
    "Danube",
    "Douro",
    "Dutch Waterways",
    "Elbe",
    "Ganges",
    "Indian Ocean",
    "Main",
    "Mediterranean",
    "Mekong",
    "Middle East",
    "Mississippi",
    "Moselle",
    "Nile",
    "Pacific",
    "Panama Canal",
    "Po",
    "Rhine",
    "Rhone",
    "Saone",
    "Seine",
    "Transatlantic",
    "West Coast US",
    "World wide",
  ];

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const [selectedTabRegion, setSelectedTabRegion] = useState(regions[0]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
  };
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  useEffect(() => {
    if (abortController) {
      abortController.abort();
    }
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    const signal = newAbortController.signal;

    if (selectedTabRegion) {
      try {
        const fetchCruises = async () => {
          try {
            setLoading(true);
            const { data } = await getAllCruisesListV2(
              {
                region: selectedTabRegion === "Asia" ? "Far East" : selectedTabRegion,
                limit: 20,
                coverImageGeometry: "300x",
              },
              { signal }, //this was add for multipe request send dicrease
            );

            if (!signal.aborted) {
              setCruisesByRegions(data);
              setLoading(false);
            }
          } catch (error) {
            console.error("Data fetching error: ", error);
          }
        };
        fetchCruises();
      } catch (error) {
        setLoading(false);
        console.error("data fetching error: ", error);
      }
    }
  }, [selectedTabRegion]);

  useEffect(() => {
    if (cruisesByRegions?.cruises && cruisesByRegions.cruises.length > 0) {
      const enhanced: CruiseWithLowestPrice[] = cruisesByRegions.cruises
        .map((cruise) => {
          const lowestPriceInfo = getLowestPriceInfo(cruise);
          const lowestPrice = parseFloat(lowestPriceInfo.price);

          return {
            ...cruise,
            lowestPrice: isNaN(lowestPrice) ? Infinity : lowestPrice,
            lowestPriceInfo,
          };
        })
        .filter((cruise) => cruise.lowestPrice !== Infinity)
        .sort((a, b) => a.lowestPrice - b.lowestPrice);

      setProcessedCruises(enhanced);
    } else {
      setProcessedCruises([]);
    }
  }, [cruisesByRegions]);
  return (
    <>
      <Container>
        <Box sx={{ mt: "3rem", mb: "1.5rem" }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.2rem", md: "3rem" },
              fontWeight: { xs: 800, md: 700 },
              color: "primary.main",
              lineHeight: { xs: 1.1, md: 1.3 },
              mb: { xs: "10px", md: "0px" },
              span: {
                color: "transparent",
                WebkitTextStrokeColor: "#CE0000",
                WebkitTextStrokeWidth: "1px",
              },
            }}
          >
            {/* Best Cruise Deals 2025/26  */}
            Best Cruise{" "}
            <span>
              Deals {new Date().getFullYear()}/{String(new Date().getFullYear() + 1).slice(-2)}
            </span>
          </Typography>
          <Box
            sx={{
              fontSize: { xs: "14px", md: "1rem" },
              fontWeight: 400,
              lineHeight: { xs: 1.4, md: 1.3 },
            }}
          >
            You can find all the best cruise deals, promotions and special offers from the top
            cruise lines in one place.
          </Box>
        </Box>

        <Box sx={{ mb: { xs: "18px", md: "16px" } }}>
          {/* <Box
            sx={{
              // color: "#7471d8",
              color: "#666",
              fontSize: "14px",
              mb: "5px",
            }}
          >
            Select a region and explore the best cruises in that area.
          </Box> */}

          <Tabs
            value={selectedTabIndex}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              // bgcolor: "#EDF1FF",
              // p: "6px",
              borderRadius: "10px",
              ".MuiTabs-root": {
                boxShadow: "0px 0px 10px 0px #06027377",
                overflow: "hidden",
              },
              ".MuiTabs-scroller": {},
              ".MuiTabs-flexContainer": {
                gap: "6px",
                ".MuiButtonBase-root.MuiTab-root": {
                  border: "1px solid #06027377",
                  textTransform: "initial",
                  borderRadius: "25px",
                  fontWeight: 700,
                  transitions: "all 0.5s",
                  color: "primary.main",
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  fontSize: "16px",
                  p: "8px 16px",
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: "#fff",
                  },
                  "&:hover": {
                    bgcolor: "#000",
                    color: "#fff",
                  },
                },
              },
              ".MuiTabs-indicator": {
                display: "none",
              },
              ".MuiButtonBase-root.MuiTabScrollButton-root.MuiTabs-scrollButtons": {
                bgcolor: "#060273",
                color: "#fff",
                opacity: 1,
                borderRadius: "10px",
                width: "35px",
                transitions: "all 0.5s",
                svg: {
                  width: "35px",
                  height: "35px",
                  p: "2px 0px 0px",
                },
                "&:nth-of-type(1)": {
                  borderRadius: "10px 0px 0px 10px",
                },
                "&:nth-of-type(4)": {
                  borderRadius: "0px 10px 10px 0px",
                },
                "&.Mui-disabled": {
                  opacity: 0,
                  width: "0px",
                },
              },
            }}
          >
            {regions.map((region, xi) => (
              <Tab
                key={xi}
                label={toTitleCase(region)}
                {...a11yProps("region", xi)}
                onClick={() => {
                  setSelectedTabRegion(region);
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Box
          role="tabpanel"
          id={`region-tabpanel-${selectedTabIndex}`}
          aria-labelledby={`region-tab-${selectedTabIndex}`}
        >
          {loading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                },
                gap: "1rem",
              }}
            >
              {[1, 2, 3, 4, 5, 6].map((x, xi) => (
                <Box
                  key={xi}
                  sx={{
                    height: "218px",
                    position: "relative",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "15px",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: "calc(100% - 2rem)",
                      height: "2rem",
                      borderRadius: "8px",
                      position: "absolute",
                      top: "1rem",
                      left: "1rem",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: "100px",
                      height: "2rem",
                      borderRadius: "8px",
                      position: "absolute",
                      bottom: "1rem",
                      left: "1rem",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: "150px",
                      height: "2.5rem",
                      borderRadius: "8px",
                      position: "absolute",
                      bottom: "1rem",
                      right: "1rem",
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : processedCruises.length > 0 ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                img: {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "flex",
                },
                ".hero_swiper": {
                  width: "100%",
                  height: "100%",
                  border: "2px solid",
                  borderColor: "primary.main",
                  borderRadius: "15px",
                  overflow: "hidden",
                  position: "relative",
                  p: "1rem",
                },
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                },
                gap: "1rem",
              }}
            >
              {processedCruises.slice(0, 6).map((cruise, i) => (
                <CruiseSummaryCard key={i} cruise={cruise} lowestPriceInfo={cruise.lowestPriceInfo} />
              ))}
            </Box>
          ) : (
            <Alert severity="error">No data available</Alert>
          )}
        </Box>

        <Box sx={{ mt: "3rem", display: { xs: "block", md: "none" } }}>
          <CruiseLineCard />
        </Box>
      </Container>
    </>
  );
};

export default BookOnlineCruiseDeals;
